import type { Lang } from "@/i18n/translations";
import { companyPaths } from "@/data/company";

export type DrugSampleKey = "saliv" | "urin";
export type DrugPurpose = "privat" | "intyg";

export const drugTestingPaths = {
  hub: { sv: "/drogtestning", en: "/en/drug-testing" },
  privat: { sv: "/drogtestning/privat", en: "/en/drug-testing/private" },
  forIntyg: { sv: "/drogtestning/for-intyg", en: "/en/drug-testing/for-certificate" },
  salivtest: { sv: "/lakarintyg/salivtest", en: "/en/medical-certificates/saliva" },
  urinprov: { sv: "/lakarintyg/urinprov", en: "/en/medical-certificates/urine" },
  forfragan: { sv: "/drogtestning/forfragan", en: "/en/drug-testing/enquiry" },
  bokaSaliv: { sv: "/drogtestning/boka/salivtest", en: "/en/drug-testing/book/saliva" },
  bokaUrin: { sv: "/drogtestning/boka/urinprov", en: "/en/drug-testing/book/urine" },
} as const;

/** HLTHY booking calendars for private sample packages. */
export const SALIVA_DRUG_BOOKING_URL = "https://intygslakaren.hlthy.se/booking?q=PlPFyv_H";
export const URINE_DRUG_BOOKING_URL = "https://intygslakaren.hlthy.se/booking?q=0AQi8nP4";

export const privateDrugBookingUrl = (type: DrugSampleKey): string =>
  type === "saliv" ? SALIVA_DRUG_BOOKING_URL : URINE_DRUG_BOOKING_URL;

export const privateDrugPackages: Record<
  DrugSampleKey,
  {
    slug: { sv: string; en: string };
    label: { sv: string; en: string };
    price: { sv: string; en: string };
    analytes: { sv: string[]; en: string[] };
    description: { sv: string; en: string };
    intro: { sv: string; en: string };
    preparation: { sv: string[]; en: string[] };
  }
> = {
  saliv: {
    slug: { sv: "salivtest", en: "saliva" },
    label: { sv: "Salivtest", en: "Saliva test" },
    price: { sv: "1 500 kr", en: "1,500 SEK" },
    analytes: {
      sv: ["Amfetamin", "Bensodiazepiner (benso)", "THC", "Kokain", "Ecstasy", "Opiater"],
      en: ["Amphetamine", "Benzodiazepines", "THC", "Cocaine", "Ecstasy", "Opiates"],
    },
    description: {
      sv: "Prov från munvätska med ett särskilt provtagningskit.",
      en: "Oral fluid sample collected with a dedicated sampling kit.",
    },
    intro: {
      sv: "Drogtest i saliv tas genom att samla munvätska med ett särskilt provtagningskit. Provet skickas till laboratoriet för analys.",
      en: "A saliva drug test collects oral fluid with a dedicated kit. The sample is sent to the laboratory for analysis.",
    },
    preparation: {
      sv: [
        "Ta med giltig legitimation.",
        "Följ instruktionerna om mat, dryck och annat intag före provtagningen.",
        "Lämna läkemedelsuppgifter till vården när de efterfrågas.",
      ],
      en: [
        "Bring valid ID.",
        "Follow instructions about food, drink and other intake before sampling.",
        "Provide medication details to care staff when asked.",
      ],
    },
  },
  urin: {
    slug: { sv: "urinprov", en: "urine" },
    label: { sv: "Urinprov", en: "Urine test" },
    price: { sv: "1 500 kr", en: "1,500 SEK" },
    analytes: {
      sv: ["Opiater", "Kokain", "Amfetamin", "THC"],
      en: ["Opiates", "Cocaine", "Amphetamine", "THC"],
    },
    description: {
      sv: "Urinprov enligt de provtagningsrutiner som gäller för ärendet.",
      en: "Urine sample according to the sampling routines that apply to the case.",
    },
    intro: {
      sv: "Drogtest i urin innebär att du lämnar ett urinprov som skickas till laboratoriet för analys. Provet tas och hanteras enligt instruktionerna för ditt ärende.",
      en: "A urine drug test means providing a urine sample that is sent to the laboratory. Collection follows the routines for your case.",
    },
    preparation: {
      sv: [
        "Ta med giltig legitimation.",
        "Följ mottagningens instruktioner före provtagningen.",
        "Vid arbetsplatsprover och vissa intyg kan övervakad provtagning behövas.",
        "Lämna läkemedelsuppgifter till vården när de efterfrågas.",
      ],
      en: [
        "Bring valid ID.",
        "Follow the clinic’s instructions before sampling.",
        "Workplace and some certificate cases may require supervised collection.",
        "Provide medication details to care staff when asked.",
      ],
    },
  },
};

export const drugSampleFromPathSlug = (slug: string | undefined): DrugSampleKey | null => {
  if (slug === "salivtest" || slug === "saliva") return "saliv";
  if (slug === "urinprov" || slug === "urine") return "urin";
  return null;
};

export const drugTestingHref = (key: keyof typeof drugTestingPaths, lang: Lang) =>
  drugTestingPaths[key][lang];

export const privateDrugBookingHref = (type: DrugSampleKey, lang: Lang, purpose: DrugPurpose = "privat") => {
  const base = type === "saliv" ? drugTestingPaths.bokaSaliv[lang] : drugTestingPaths.bokaUrin[lang];
  return purpose === "intyg" ? `${base}?andamal=intyg` : base;
};

export const privateDrugEnquiryHref = (
  lang: Lang,
  purpose: DrugPurpose = "privat",
  sample?: DrugSampleKey | "ej-valt"
) => {
  const params = new URLSearchParams();
  params.set("andamal", purpose);
  if (sample && sample !== "ej-valt") params.set("prov", sample);
  return `${drugTestingPaths.forfragan[lang]}?${params.toString()}`;
};

export const companyDrugQuoteHref = (lang: Lang) =>
  `${companyPaths.kontakt[lang]}?tjanst=drogtest-forebyggande`;
